import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, CaretRight, Clock } from "@phosphor-icons/react";
import { listEvents, listMyRsvps, formatEventWhen, RSVP_STATUS_META } from "@/lib/events";

/**
 * Parent · Dashboard card — next up to 3 upcoming events with their
 * current RSVP state. Silent on failure (no card rendered). Deep-links
 * straight to /parent/events.
 */
export default function UpcomingEventsCard() {
  const [events, setEvents] = useState([]);
  const [rsvps, setRsvps] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const rows = await listEvents({ upcomingOnly: true, limit: 3 });
        if (!alive) return;
        setEvents(rows);
        const map = await listMyRsvps(rows.map((e) => e.id));
        if (!alive) return;
        setRsvps(map);
      } catch {
        /* fail silent */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading) return null;
  if (events.length === 0) return null;

  return (
    <Link
      to="/parent/events"
      data-testid="parent-upcoming-events-card"
      className="block rounded-2xl border border-neutral-200 bg-white p-5 transition-all hover:-translate-y-[2px] hover:shadow-md active:scale-[0.99]"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarCheck size={16} weight="duotone" className="text-primary-hover" />
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            Upcoming events
          </p>
        </div>
        <CaretRight size={14} weight="bold" className="text-neutral-400" />
      </div>
      <ul className="space-y-3">
        {events.map((ev) => {
          const status = rsvps[ev.id]?.status;
          return (
            <li
              key={ev.id}
              data-testid={`parent-upcoming-event-${ev.id}`}
              data-rsvp-status={status || ""}
              className="flex items-start justify-between gap-3"
            >
              <div className="min-w-0">
                <p className="truncate font-heading text-sm font-semibold text-neutral-900">
                  {ev.title}
                </p>
                <p className="mt-0.5 flex items-center gap-1 text-xs text-neutral-500">
                  <Clock size={11} weight="duotone" />
                  {formatEventWhen(ev.starts_at, ev.ends_at)}
                </p>
              </div>
              {status ? (
                <span
                  className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${RSVP_STATUS_META[status].tone}`}
                >
                  {RSVP_STATUS_META[status].label}
                </span>
              ) : (
                ev.rsvp_enabled && (
                  <span className="shrink-0 rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-hover">
                    RSVP
                  </span>
                )
              )}
            </li>
          );
        })}
      </ul>
    </Link>
  );
}
